import Vue from "vue";
import axios from 'axios';
import {Decimal} from 'decimal.js';

import {cashoutEventBus, CashoutEvents, CheckoutErrorMode} from "./bus";

import {validateEmail} from "./util/email";
import {
    DECIMAL_PLACES_CRYPTO,
    DECIMAL_PLACES_FIAT,
    formatCurrency,
    NEAREST_CRYPTO,
    NEAREST_FIAT,
} from "./util/number";

let sha1 = require("sha1");

export interface FieldErrors {
    [key: string]: Array<string>;
}

interface voidCallback {
    (): void
}

interface UserData {
    id: string;
    avatarUrl: string;
    email: string;
    countryCode: string,
}

export function loadUserData(userDataJson: any): UserData {
    return {
        id: userDataJson.id,
        avatarUrl: userDataJson.avatarUrl,
        email: userDataJson.email,
        countryCode: userDataJson.countryCode,
    }
}

interface WalletData {
    walletBalance: Decimal;
    walletCurrency: string;
    nativeBalance: Decimal;
    nativeCurrency: string;
}

export function loadWalletData(walletDataJson: any): WalletData {
    return {
        walletBalance: new Decimal(walletDataJson.walletBalance),
        walletCurrency: walletDataJson.walletCurrency,
        nativeBalance: new Decimal(walletDataJson.nativeBalance),
        nativeCurrency: walletDataJson.nativeCurrency
    } as WalletData
}

export interface Tag {
    code: string;
    name: string;
}

export interface CountryTag extends Tag {
    currencyCode: string;
}

export let countryTags: Array<CountryTag>;
export let categoryTags: Array<Tag>;

export function loadTags(countryTagsJson: any, categoryTagsJson: any): void {
    countryTags = countryTagsJson.map((tagJson: any) => {
        return {code: tagJson.code, name: tagJson.name, currencyCode: tagJson.currencyCode}
    });
    categoryTags = categoryTagsJson.map((tagJson: any) => {
        return {code: tagJson.code, name: tagJson.name}
    });
}

export function getTag(code: string, tagList: Array<Tag>): Tag | undefined {
    let foundTag: Tag | undefined = undefined;
    tagList.some((tag) => {
        if (tag.code === code) {
            foundTag = tag;
            return true
        }
        return false;
    });
    return foundTag
}

export interface PurchaseInstruction {
    skuId: number;
    cryptoCurrencyCode: string;
    cryptoAmount: string;
    fiatCurrencyCode: string;
    fiatAmount: string;
    deliveryEmail: string;
    cashoutValue: string;
    twoFactorCode: string;
    idempotencyKey: string;
    marketingConsent: boolean;
}

export interface PurchaseInstructionResponse {
    wasSuccessful: boolean;
    errorMode: CheckoutErrorMode | null;
    message: string | null;
    reference: string | null;
}


export class CashoutData {
    debug: boolean = false;

    cryptoCurrencyCode: string;
    fiatCurrencyCode: string;
    fxRate: number | undefined;
    transactionFeeMultiplier: number;

    private _cashoutCountryCode: string;

    private _fiatAmount: Decimal;
    private _cryptoAmount: Decimal;

    minCryptoAmount: Decimal = new Decimal('0.01');
    maxFiatAmount: Decimal = new Decimal('5000');

    feeSavingMultiplier: Decimal = new Decimal('0.016');

    // I think we did it like this to make these vars more observable
    selectedSkuId: number | null;
    // Stores the sku id after it's been forcibly deselected, means it can be reselected if the cashout values come
    // back into range (user clears box from 50 and types 60)
    lastSelectedSkuId: number | null = null;

    deliveryEmail: string = "";
    marketingConsent: boolean = false;

    rateManager: RateManager;

    userData: UserData;
    walletData: WalletData;

    orderInProgress: boolean = false;

    twoFactorCode: string = "";

    constructor(userData: any, walletData: any) {
        this.userData = loadUserData(userData);
        this.walletData = loadWalletData(walletData);

        this.cryptoCurrencyCode = this.walletData.walletCurrency;
        this.rateManager = new RateManager(this.cryptoCurrencyCode);

        this._fiatAmount = new Decimal(0);
        this._cryptoAmount = new Decimal(0);

        this._cashoutCountryCode = this.userData.countryCode;
        let countryTag = getTag(this._cashoutCountryCode, countryTags) as CountryTag;
        if (countryTag) {
            this.fiatCurrencyCode = countryTag.currencyCode;
        } else {
            this.fiatCurrencyCode = "GBP";
        }

        // We have removed fees
        this.transactionFeeMultiplier = 0;

        this.selectedSkuId = 0;

        this.deliveryEmail = this.userData.email;
    }

    get cashoutCountryCode(): string {
        return this._cashoutCountryCode
    }

    set cashoutCountryCode(countryCode: string) {
        this._cashoutCountryCode = countryCode;
        let countryTag = getTag(countryCode, countryTags) as CountryTag;
        if (!countryTag) throw new Error(`Cannot find country tag for code '${countryCode}'`);
        this.fiatCurrencyCode = countryTag.currencyCode;
        Vue.set(this, 'fxRate', this.rateManager.getRate(this.fiatCurrencyCode));
        this.cryptoAmount = this.cryptoAmount;
    }

    get walletBalanceAsFiat(): Decimal | undefined {
        if (!this.fxRate) return undefined;
        return this.walletData.walletBalance.times(1 / this.fxRate);
    }

    asCrypto(fiat: Decimal): Decimal {
        if (!this.fxRate) throw new Error('Cannot update fiat amount until we have an fxRate');
        return fiat.times(this.fxRate).toNearest(NEAREST_CRYPTO)
    }

    asFiat(crypto: Decimal): Decimal {
        if (!this.fxRate) throw new Error('Cannot update crypto amount until we have an fxRate');
        return crypto.times(1 / this.fxRate)
    }

    get minFiatAmount(): Decimal | undefined {
        if (!this.fxRate) return undefined;
        return this.asFiat(this.minCryptoAmount)
    }

    get fiatAmount(): Decimal {
        return this._fiatAmount
    }

    set fiatAmount(value: Decimal) {
        if (!this.fxRate) throw new Error('Cannot update fiat amount until we have an fxRate');
        if (value == null) throw new Error('Cannot null fiat amount'); // Prevent nulling this variable, it breaks things
        if (value.greaterThan(10000)) value = new Decimal(10000);
        this._cryptoAmount = this.asCrypto(value);
        this._fiatAmount = value;
    }

    get cryptoAmount(): Decimal {
        return this._cryptoAmount
    }

    set cryptoAmount(value: Decimal) {
        if (!this.fxRate) throw new Error('Cannot update crypto amount until we have an fxRate');
        if (value == null) throw new Error('Cannot null crypto amount'); // Prevent nulling this variable, it breaks things
        if (value.greaterThan(5000 * this.fxRate)) value = new Decimal(5000 * this.fxRate);
        this._cryptoAmount = value;
        this._fiatAmount = this.asFiat(value);
    }

    get sku(): Sku | null {
        if (this.selectedSkuId === null) return null;
        return modelSkuMap[this.selectedSkuId]
    }

    get lastSku(): Sku | null {
        if (this.lastSelectedSkuId === null) return null;
        return modelSkuMap[this.lastSelectedSkuId]
    }

    get cashoutValueGross(): Decimal | null {
        if (this.sku === null) {
            return null
        }
        return this.sku.cashoutValue(this.fiatAmount)
    }

    get bonusAmount(): Decimal | null {
        if (this.cashoutValueGross === null) return null;
        return this.cashoutValueGross.minus(this.fiatAmount);
    }

    get bonusAmountDisplay(): Decimal | null {
        // Due to rounding the normal bonusAmount may be unsuitable to show to the user
        // i.e. 3.57 (3.5659) + 0.04 (0.03610) bonus = 3.60 (3.6020) ≠ 3.61
        if (!this.cashoutValueGross || !this.bonusAmount) return null;
        return this.cashoutValueGross.toNearest(NEAREST_FIAT).minus(
            this.fiatAmount.toNearest(NEAREST_FIAT)).toNearest(NEAREST_FIAT)
    }

    get transactionFee(): Decimal | null {
        if (this.cashoutValueGross === null) return null;
        return this.cashoutValueGross.times(this.transactionFeeMultiplier);
    }

    get cashoutValueNett(): Decimal | null {
        if (this.cashoutValueGross === null || this.transactionFee === null) return null;
        return this.cashoutValueGross.minus(this.transactionFee);
    }

    get feeSavingAmount(): Decimal | null {
        if (!this.fiatAmount || !this.fxRate) return null;
        return this.fiatAmount.times(this.feeSavingMultiplier)
    }

    get idempotencyKey(): string {
        let timestamp = Date.now().toString();
        return sha1(`${this.userData.email}${timestamp}`) as string;
    }

    updateUserData() {
        axios.get(`/jsonapi/crypto-user-data`)
            .then(response => {
                if (!response.data.userData || !response.data.walletData)
                    throw "Invalid response from crypto-user-data";
                this.userData = loadUserData(response.data.userData);
                this.walletData = loadWalletData(response.data.walletData);
            })
            .catch(error => {
                console.error(`Failed to update user data: ${error}`)
            })
    }

    updateRate(first: boolean = false) {
        this.rateManager.fetchNewRate(this.cryptoCurrencyCode, this.fiatCurrencyCode, () => {
            // Use Vue.set to ensure everything that depends on this var is updated
            // Unsure why it wasn't, perhaps because it starts life as undefined?
            Vue.set(this, 'fxRate', this.rateManager.getRate(this.fiatCurrencyCode));
            if (!first) {
                // Fix fiat and adjust crypto
                this.fiatAmount = this.fiatAmount;
            }
        })
    }

    get purchaseRequestValid(): Array<string> {
        let errors: Array<string> = [];
        if (!validateEmail(this.deliveryEmail)) errors.push("The delivery email is invalid");
        if (this.cryptoAmount.gt(this.walletData.walletBalance)) errors.push("Not enough crypto to cover this purchase");
        if (this.cryptoAmount.lt(this.minCryptoAmount)) errors.push(`Cashout must be at least ${this.minCryptoAmount} ${this.cryptoCurrencyCode}`);
        if (this.fiatAmount.gt(this.maxFiatAmount)) errors.push(`Cashout must be no greater than ${formatCurrency(this.maxFiatAmount, this.fiatCurrencyCode)}`);
        if (!this.selectedSkuId) errors.push("Product not selected");
        return errors;
    }

    get purchaseInstruction(): PurchaseInstruction {
        if (!this.cashoutValueNett) throw 'Cannot create purchase instruction, cashoutValueNett is null';
        if (!this.selectedSkuId) throw 'Cannot create purchase instruction, selectedSkuId is null';
        return {
            skuId: this.selectedSkuId,
            cryptoCurrencyCode: this.cryptoCurrencyCode,
            cryptoAmount: this.cryptoAmount.toFixed(DECIMAL_PLACES_CRYPTO).toString(),
            fiatCurrencyCode: this.fiatCurrencyCode,
            fiatAmount: this.fiatAmount.toFixed(DECIMAL_PLACES_FIAT).toString(),
            deliveryEmail: this.deliveryEmail,
            cashoutValue: this.cashoutValueNett.toFixed(DECIMAL_PLACES_FIAT).toString(),
            twoFactorCode: this.twoFactorCode,
            idempotencyKey: this.idempotencyKey,
            marketingConsent: this.marketingConsent,
        }
    }

    getSkuFromId(skuId: number): Sku | undefined {
        return modelSkuMap[skuId]
    }

}

export enum ProductAvailability {
    REALTIME = "realtime",
    STOCKED = "stocked",
}

export enum ProductDenominationType {
    OPEN = "open",
    FIXED = "fixed",
}

export class Sku {
    id: number;
    code: string;
    name: string;
    imageUrl: string;
    percentDiscount: Decimal;
    minimumValue: Decimal;
    maximumValue: Decimal;
    availability: ProductAvailability;
    denominationType: ProductDenominationType;
    availableDenominations: Array<Decimal> | undefined;
    currencyCode: string;
    countryTagCodes: string[];
    categoryTagCodes: string[];

    descriptionHtml: string;
    tcHtml: string;
    redeemHtml: string;


    constructor(skuData: any) {
        this.id = skuData.id;
        this.code = skuData.code;
        this.name = skuData.name;
        this.imageUrl = skuData.imageUrl;
        this.percentDiscount = new Decimal(skuData.percentDiscount);
        this.minimumValue = new Decimal(skuData.minimumValue);
        this.maximumValue = new Decimal(skuData.maximumValue);

        this.availability = skuData.availability;
        this.denominationType = skuData.denominationType;

        this.currencyCode = skuData.currency;
        this.countryTagCodes = skuData.countries;
        this.categoryTagCodes = skuData.categories;

        this.descriptionHtml = skuData.descriptionHtml;
        this.tcHtml = skuData.tcHtml;
        this.redeemHtml = skuData.redeemHtml;
    }

    get isStocked(): boolean {
        return (this.availability === ProductAvailability.STOCKED)
    }

    get valueMultiplier(): Decimal {
        return new Decimal(100).minus(this.percentDiscount).dividedBy(100)
    }

    get countryCode(): string {
        // TODO Remove: Used only to give a single country when checking if a country has Skus, see `countryHasSkus`
        let codeParts = this.code.split('-');
        return codeParts[codeParts.length - 1];
    }

    imageUrlForSize(size: number, format: string): string {
        return `${this.imageUrl}-${size}.${format}`
    }

    cashoutValue(fiatAmount: Decimal): Decimal {
        return fiatAmount.dividedBy(this.valueMultiplier)
    }

    fiatValue(cashoutAmount: Decimal): Decimal {
        return cashoutAmount.times(this.valueMultiplier)
    }

    filterAvailableDenominations(minFiat: Decimal): Array<Decimal> | undefined {
        if (!this.availableDenominations) return undefined;
        // throw this.cashoutValue(minFiat);
        let minCashout = Decimal.max(this.cashoutValue(minFiat), this.minimumValue);
        return this.availableDenominations.filter(d => d.gte(minCashout))
    }

    inMinMaxRange(cashoutValue: Decimal, minFiat: Decimal): boolean {
        let minCashout = this.cashoutValue(minFiat);
        // Round the cashout value
        cashoutValue = cashoutValue.toNearest(NEAREST_FIAT);
        let minValue = Decimal.max(minCashout, this.minimumValue).toNearest(NEAREST_FIAT);
        // Is the value within range?
        return (cashoutValue.gte(minValue) && cashoutValue.lte(this.maximumValue));
    }

    loadAvailableDenominations(): Promise<void> {
        if (this.availability !== ProductAvailability.STOCKED)
            throw new Error("Can only load availableDenominations for STOCKED products");
        return axios.get(`/jsonapi/product-available-denominations/${this.code}`)
            .then(response => {
                this.availableDenominations = response.data.availableDenominations.map((x: string) => new Decimal(x));
            })
            .catch(error => {
                throw new Error(`Failed to fetch product-available-denominations: ${error}`)
            })
    }

    availableForValue(cashoutValue: Decimal, minFiat: Decimal): Promise<boolean> {
        // Round the cashout value
        cashoutValue = cashoutValue.toNearest(NEAREST_FIAT);
        // Cannot use .include when we have Decimal objects rather than numbers, so search the array for an equal value
        let availableDenominationsIncludes = (value: Decimal, ds: Array<Decimal>) => ds.some(d => d.eq(value));
        return new Promise((resolve, reject) => {
            // Whatcha doing here punk?
            if (this.availability === ProductAvailability.REALTIME)
                return resolve(this.inMinMaxRange(cashoutValue, minFiat));
            if (this.availableDenominations === undefined) {
                cashoutEventBus.$emit(CashoutEvents.UI_BLOCK);
                this.loadAvailableDenominations().then(() => {
                    cashoutEventBus.$emit(CashoutEvents.UI_UNBLOCK);
                    if (this.availableDenominations === undefined) {
                        reject("availableDenominations undefined");
                        return
                    }
                    resolve(availableDenominationsIncludes(cashoutValue, this.filterAvailableDenominations(minFiat)!));
                });
            } else {
                resolve(availableDenominationsIncludes(cashoutValue, this.filterAvailableDenominations(minFiat)!))
            }
        })
    }

}


// Load the json sku data into Sku objects

// TODO There must be a better way of doing this
let modelSkus: Array<Sku>;

export function loadSkus(skuData: Array<any>): Array<Sku> {
    let skus = [];
    for (let singleSkuData of skuData) {
        skus.push(new Sku(singleSkuData))
    }
    modelSkus = skus;
    return skus
}

// Build a map of SKUs for reverse lookup

interface SkuMap {
    [key: number]: Sku;
}

// TODO There must be a better way of doing this
let modelSkuMap: SkuMap;

export function makeSkuMap(skus: Array<Sku>): SkuMap {
    let skuMap: SkuMap = {};
    for (let sku of skus) {
        skuMap[sku.id] = sku
    }
    modelSkuMap = skuMap;
    return skuMap
}

export function countryHasSkus(countryTag: CountryTag): boolean {
    return modelSkus.filter(sku => sku.countryCode == countryTag.code).length > 0;
}

interface RateMap {
    // Map currencyCode to a rate
    [key: string]: number;
}

class RateManager {
    cryptoCode: string;
    rates: RateMap = {};

    constructor(cryptoCode: string) {
        this.cryptoCode = cryptoCode
    }

    fetchNewRate(cryptoCode: string, fiatCode: string, callback: voidCallback) {
        axios.get(`/jsonapi/crypto-rate?cryptoCode=${cryptoCode}&fiatCode=${fiatCode}`)
            .then(response => {
                Object.keys(response.data.rates).forEach((currencyCode) => {
                    this.rates[currencyCode] = parseFloat(response.data.rates[currencyCode])
                });
            })
            .then(callback)
            .catch(error => {
                throw new Error(`Failed to fetch rate: ${error}`)
            })
    }

    getRate(currencyCode: string): number {
        if (!this.rates.hasOwnProperty(currencyCode)) throw new Error(`No rate in RateManager for ${currencyCode}`)
        return (1 / this.rates[currencyCode]);
    }
}

export interface SuggestedCashoutValue {
    cashoutValue: Decimal
    fiatValue: Decimal
    fiatCurrency: string
    cryptoValue: Decimal
    cryptoCurrency: string
}

function makeSuggestedCashoutValue(sku: Sku, fiatValue: Decimal, cashoutData: CashoutData): SuggestedCashoutValue {
    if (!cashoutData.fxRate) throw 'No fx rate';
    return {
        cashoutValue: sku.cashoutValue(fiatValue),
        fiatValue: fiatValue,
        fiatCurrency: sku.currencyCode,
        cryptoValue: fiatValue.times(cashoutData.fxRate).toNearest(NEAREST_CRYPTO),
        cryptoCurrency: cashoutData.cryptoCurrencyCode,
    };
}


export function suggestNewCashoutValues(sku: Sku, cashoutData: CashoutData): SuggestedCashoutValue[] {
    if (!cashoutData.walletBalanceAsFiat) return [];
    if (!cashoutData.minFiatAmount) return [];

    let minFiatForThisProduct = Decimal.max(cashoutData.minFiatAmount, sku.fiatValue(sku.minimumValue));
    let maxFiatForThisProduct = Decimal.min(cashoutData.walletBalanceAsFiat, sku.fiatValue(sku.maximumValue));

    // Come back when you get some money, buddy!
    if (cashoutData.walletBalanceAsFiat.lt(minFiatForThisProduct))
        return [];

    if (!sku.availableDenominations) {
        // Realtime, suggest min or max
        if (cashoutData.fiatAmount.gte(maxFiatForThisProduct))
            return [makeSuggestedCashoutValue(sku, maxFiatForThisProduct, cashoutData)];
        else if (cashoutData.fiatAmount.lt(minFiatForThisProduct))
            return [makeSuggestedCashoutValue(sku, minFiatForThisProduct, cashoutData)];
        else {
            // How have you even got here?
            throw new Error(`How have you even got here? ${minFiatForThisProduct.toNumber()}, ${maxFiatForThisProduct.toNumber()}`);
            return [];
        }
    }

    // Filter availableDenominations to the ones the user can afford and are above the min spend
    let availableDenominations = sku.availableDenominations.filter((x: Decimal) => (
        sku.fiatValue(x).lte(maxFiatForThisProduct) && sku.fiatValue(x).gte(minFiatForThisProduct)));

    // Iterate over availableDenominations forwards to find the closest lower denomination to the currently set fiatAmount
    let lowerFiatValue: Decimal | undefined;
    let upperFiatValue: Decimal | undefined;
    availableDenominations.forEach((cashoutValue => {
        // 20 → 18.87
        let cashoutValueAsFiat = sku.fiatValue(cashoutValue);
        if (cashoutValueAsFiat.lessThan(cashoutData.fiatAmount)) lowerFiatValue = cashoutValueAsFiat;
    }));
    // Iterate over availableDenominations backwards to find the closest upper denomination to the currently set fiatAmount
    ([] as Decimal[]).concat(availableDenominations).reverse().forEach((cashoutValue => {
        // 20 → 18.87
        let cashoutValueAsFiat = sku.fiatValue(cashoutValue);
        if (cashoutValueAsFiat.greaterThan(cashoutData.fiatAmount)) upperFiatValue = cashoutValueAsFiat;
    }));

    let suggestions: SuggestedCashoutValue[] = [];
    if (lowerFiatValue)
        suggestions.push(makeSuggestedCashoutValue(sku, lowerFiatValue, cashoutData));
    if (upperFiatValue)
        suggestions.push(makeSuggestedCashoutValue(sku, upperFiatValue, cashoutData));

    return suggestions;
}

